import React, { useState, useEffect, useCallback } from 'react';
import Map, { Marker } from 'react-map-gl/maplibre';
import { TextField, Box, Typography, Button, Paper, CircularProgress, Autocomplete } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Country, City } from 'country-state-city';
import 'maplibre-gl/dist/maplibre-gl.css';

// Функция для получения координат города по названию
const getCityCoordinates = (cityName) => {
  if (!cityName) return null;
  
  const normalizedCityName = cityName.toLowerCase().trim();
  
  // Получаем все страны
  const countries = Country.getAllCountries();
  
  // Проходим по всем странам и ищем город
  for (const country of countries) {
    const cities = City.getCitiesOfCountry(country.isoCode);
    if (!cities) continue;
    
    const city = cities.find(c => c.name.toLowerCase() === normalizedCityName);
    if (city && city.latitude && city.longitude) {
      return {
        latitude: parseFloat(city.latitude),
        longitude: parseFloat(city.longitude),
        country: country.name
      };
    }
  }
  
  // Специальные случаи для городов, которых может не быть в библиотеке
  const specialCities = {
    "dushanbe": { latitude: 38.5598, longitude: 68.7870, country: "Tajikistan" },
    "душанбе": { latitude: 38.5598, longitude: 68.7870, country: "Tajikistan" }
  };
  
  return specialCities[normalizedCityName] || null;
};

const LocationPicker = ({ 
  initialLocation = null, 
  onLocationSelect, 
  onClose,
  t = {} 
}) => {
  // Проверяем координаты на валидность
  const isValidCoordinate = (coord) => {
    return coord !== undefined && coord !== null && !isNaN(coord);
  };
  
  // Получаем начальные координаты
  const getInitialCoordinates = () => {
    // Проверяем, есть ли валидные координаты в initialLocation
    if (initialLocation?.coordinates && 
        isValidCoordinate(initialLocation.coordinates.longitude) && 
        isValidCoordinate(initialLocation.coordinates.latitude)) {
      return {
        longitude: initialLocation.coordinates.longitude,
        latitude: initialLocation.coordinates.latitude
      };
    }
    
    // Если есть город, пробуем получить его координаты из библиотеки
    if (initialLocation?.structuredCity) {
      const cityCoords = getCityCoordinates(initialLocation.structuredCity);
      if (cityCoords) {
        return {
          longitude: cityCoords.longitude,
          latitude: cityCoords.latitude
        };
      }
    }
    
    // Значения по умолчанию - центр мира
    return {
      longitude: 0,
      latitude: 0
    };
  };
  
  const initialCoords = getInitialCoordinates();
  
  // Состояние для карты
  const [viewState, setViewState] = useState({
    longitude: initialCoords.longitude,
    latitude: initialCoords.latitude,
    zoom: 10
  });
  
  // Состояние для поиска
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Состояние для маркера - только если есть валидные координаты
  const [marker, setMarker] = useState(
    (initialLocation && initialLocation.coordinates && 
     isValidCoordinate(initialLocation.coordinates.longitude) && 
     isValidCoordinate(initialLocation.coordinates.latitude)) 
      ? {
          longitude: initialLocation.coordinates.longitude,
          latitude: initialLocation.coordinates.latitude,
          name: initialLocation.structuredCity || ''
        } 
      : null
  );
  
  // Состояние для выбранной страны и города
  const [selectedCountry, setSelectedCountry] = useState(initialLocation?.structuredCountry || '');
  const [selectedCity, setSelectedCity] = useState(initialLocation?.structuredCity || '');
  
  // Функция для поиска местоположения
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 3) return;
    
    setLoading(true);
    
    try {
      // Сначала проверяем в библиотеке городов
      const normalizedQuery = query.toLowerCase().trim();
      const cityCoords = getCityCoordinates(normalizedQuery);
      
      if (cityCoords) {
        setSearchResults([{
          display_name: normalizedQuery.charAt(0).toUpperCase() + normalizedQuery.slice(1) + 
                       (cityCoords.country ? `, ${cityCoords.country}` : ''),
          lat: cityCoords.latitude,
          lon: cityCoords.longitude,
          address: {
            city: normalizedQuery.charAt(0).toUpperCase() + normalizedQuery.slice(1),
            country: cityCoords.country
          }
        }]);
        setLoading(false);
        return;
      }
      
      // Также проверяем страны
      const countries = Country.getAllCountries();
      const countryMatch = countries.find(country => 
        country.name.toLowerCase().includes(normalizedQuery) || 
        normalizedQuery.includes(country.name.toLowerCase())
      );
      
      if (countryMatch && countryMatch.latitude && countryMatch.longitude) {
        setSearchResults([{
          display_name: countryMatch.name,
          lat: countryMatch.latitude,
          lon: countryMatch.longitude,
          address: {
            country: countryMatch.name
          }
        }]);
        setLoading(false);
        return;
      }
      
      // Если не нашли в списке крупных городов, используем Nominatim
      const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`);
      const data = await response.json();
      
      setSearchResults(data);
    } catch (error) {
      console.error('Error searching location:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Обработчик выбора результата поиска
  const handleSelectResult = (result) => {
    if (!result) return;
    
    const newMarker = {
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
      name: result.display_name
    };
    
    setMarker(newMarker);
    setViewState({
      ...viewState,
      latitude: newMarker.latitude,
      longitude: newMarker.longitude,
      zoom: 12
    });
    
    // Извлекаем страну и город из результата
    let country = '';
    let city = '';
    
    if (result.address) {
      country = result.address.country || '';
      city = result.address.city || result.address.town || result.address.village || '';
    } else {
      // Если адрес не структурирован, используем display_name
      const parts = result.display_name.split(',').map(part => part.trim());
      city = parts[0];
      country = parts[parts.length - 1];
    }
    
    setSelectedCity(city);
    setSelectedCountry(country);
  };
  
  // Обработчик клика по карте
  const handleMapClick = (event) => {
    const { lng, lat } = event.lngLat;
    
    // Создаем новый маркер
    const newMarker = {
      longitude: lng,
      latitude: lat,
      name: `${lat.toFixed(4)}, ${lng.toFixed(4)}`
    };
    
    setMarker(newMarker);
    
    // Выполняем обратное геокодирование для получения информации о месте
    reverseGeocode(lat, lng);
  };
  
  // Функция обратного геокодирования
  const reverseGeocode = async (lat, lng) => {
    try {
      setLoading(true);
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`);
      const data = await response.json();
      
      if (data && data.address) {
        const city = data.address.city || data.address.town || data.address.village || '';
        const country = data.address.country || '';
        
        setSelectedCity(city);
        setSelectedCountry(country);
        
        // Обновляем имя маркера
        setMarker(prev => ({
          ...prev,
          name: `${city}, ${country}`
        }));
      }
    } catch (error) {
      console.error('Error reverse geocoding:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Обработчик подтверждения выбора
  const handleConfirm = () => {
    if (marker && isValidCoordinate(marker.latitude) && isValidCoordinate(marker.longitude)) {
      onLocationSelect({
        structuredCity: selectedCity,
        structuredCountry: selectedCountry,
        coordinates: {
          latitude: marker.latitude,
          longitude: marker.longitude
        }
      });
      
      if (onClose) onClose();
    } else if (selectedCity || selectedCountry) {
      // Если есть город или страна, но нет координат
      onLocationSelect({
        structuredCity: selectedCity,
        structuredCountry: selectedCountry,
        coordinates: null
      });
      
      if (onClose) onClose();
    }
  };
  
  // Эффект для инициализации поиска при изменении запроса
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3) {
        handleSearch(searchQuery);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);
  
  return (
    <Box sx={{ width: '100%', height: '500px', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <Autocomplete
          fullWidth
          freeSolo
          options={searchResults}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option.display_name || '';
          }}
          loading={loading}
          onInputChange={(event, value) => setSearchQuery(value)}
          onChange={(event, value) => {
            if (value && typeof value !== 'string') {
              handleSelectResult(value);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t.searchLocation || "Search location"}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <li {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ color: 'text.secondary', mr: 1 }} />
                <Typography variant="body2">{option.display_name}</Typography>
              </Box>
            </li>
          )}
        />
      </Box>
      
      <Paper sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <Map
          {...viewState}
          onMove={evt => setViewState(evt.viewState)}
          style={{ width: '100%', height: '100%' }}
          mapStyle="https://demotiles.maplibre.org/style.json" // Бесплатный стиль карты
          onClick={handleMapClick}
        >
          {marker && isValidCoordinate(marker.longitude) && isValidCoordinate(marker.latitude) && (
            <Marker
              longitude={marker.longitude}
              latitude={marker.latitude}
              color="red"
              anchor="bottom"
            >
              <LocationOnIcon sx={{ color: 'error.main', fontSize: 36, transform: 'translate(-50%, -100%)' }} />
            </Marker>
          )}
        </Map>
      </Paper>
      
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          {marker && (
            <Typography variant="body2">
              {selectedCity && selectedCountry 
                ? `${selectedCity}, ${selectedCountry}` 
                : (marker.name || t.locationSelected || "Location selected")}
            </Typography>
          )}
          {!marker && (
            <Typography variant="body2" color="text.secondary">
              {t.noLocationSelected || "No location selected. Click on the map or search for a location."}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={onClose}
          >
            {t.cancel || "Cancel"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={!marker || !isValidCoordinate(marker.longitude) || !isValidCoordinate(marker.latitude)}
          >
            {t.confirm || "Confirm Location"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default LocationPicker;
